import React from 'react';
import styled from 'styled-components';
import { User } from '../types';
import EmojiConvertor from 'emoji-js';
import { formatUserLocalTime, getTimezoneAbbreviation, isInMountainTime, formatCompactTime } from '../utils/timezone';

interface UserAvatarProps {
  user: User;
  workspaceTeamId?: string;
}

const AvatarContainer = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  cursor: pointer;

  @media (min-width: 480px) {
    width: 65px;
    height: 65px;
  }

  @media (min-width: 768px) {
    width: 70px;
    height: 70px;
  }

  &::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.7rem;
    white-space: pre-line;
    max-width: 180px;
    text-align: center;
    line-height: 1.3;
    opacity: 0;
    visibility: hidden;
    z-index: 99999;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    pointer-events: none;
    
    @media (min-width: 768px) {
      bottom: calc(100% + 15px);
      padding: 8px 12px;
      border-radius: 8px;
      font-size: 0.8rem;
      max-width: 200px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
    opacity: 0;
    visibility: hidden;
    z-index: 99999;
    
    @media (min-width: 768px) {
      bottom: calc(100% + 9px);
      border: 6px solid transparent;
      border-top-color: rgba(0, 0, 0, 0.9);
    }
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

const Avatar = styled.img<{ $isOffline?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  object-fit: cover;
  opacity: ${props => props.$isOffline ? 0.4 : 1};
  filter: ${props => props.$isOffline ? 'grayscale(70%) brightness(0.8)' : 'none'};
  transition: opacity 0.3s ease, filter 0.3s ease;
`;


const StatusEmojiBadge = styled.div<{ $isOffline?: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  opacity: ${props => props.$isOffline ? 0.4 : 1};
  transition: opacity 0.3s ease;
  
  @media (min-width: 480px) {
    top: -5px;
    right: -5px;
    width: 26px;
    height: 26px;
    font-size: 13px;
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.4);
  }
  
  @media (min-width: 768px) {
    top: -5px;
    right: -5px;
    width: 28px;
    height: 28px;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
`;

const ClockBadge = styled.div<{ $isOffline?: boolean }>`
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  border: 1.5px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  min-width: 28px;
  text-align: center;
  opacity: ${props => props.$isOffline ? 0.4 : 1};
  transition: opacity 0.3s ease;
  
  @media (min-width: 480px) {
    bottom: -7px;
    padding: 2px 7px;
    font-size: 10px;
    border-radius: 9px;
    min-width: 30px;
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.35);
  }
  
  @media (min-width: 768px) {
    bottom: -8px;
    padding: 3px 8px;
    font-size: 11px;
    border-radius: 10px;
    min-width: 32px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4);
  }
`;

export const UserAvatar: React.FC<UserAvatarProps> = ({ user, workspaceTeamId }) => {
  // Check if user should show clock badge (not in Mountain Time)
  const showClockBadge = !isInMountainTime(user.tz, user.tzLabel, user.tzOffset);
  const compactTime = showClockBadge ? formatCompactTime(user.tz, user.tzOffset) : '';
  
  // Convert Slack emoji format to actual emoji using emoji-js
  const getEmojiDisplay = (statusEmoji: string | undefined) => {
    if (!statusEmoji) return '';
    
    // If it's already a Unicode emoji (not wrapped in colons), return as-is
    if (!statusEmoji.startsWith(':') || !statusEmoji.endsWith(':')) {
      return statusEmoji;
    }
    
    // Create emoji converter instance
    const emoji = new EmojiConvertor();
    emoji.replace_mode = 'unified';
    emoji.allow_native = true;
    
    // Convert emoji shortcode to Unicode
    const converted = emoji.replace_colons(statusEmoji);
    
    // If conversion failed (same as input), it's likely a custom emoji - show chat bubble
    return converted !== statusEmoji ? converted : '💬';
  };
  
  const displayEmoji = getEmojiDisplay(user.statusEmoji);
  
  // Create tooltip text for data attribute - use newlines for stacking (removed timezone info)
  const displayEmojiForTooltip = user.statusEmoji ? getEmojiDisplay(user.statusEmoji) : '';
  const tooltipText = [
    user.displayName,
    user.title && user.title,
    user.isInMeeting && user.statusText && `📅 ${user.statusText}`,
    user.isOutOfOffice && user.statusText && `🏖️ ${user.statusText}`,
    user.hasCustomStatus && user.statusText && !user.isInMeeting && !user.isOutOfOffice && `${displayEmojiForTooltip} ${user.statusText}`
  ].filter(Boolean).join('\n');

  const handleClick = () => {
    // Create Slack deep link to user's profile/DM
    let slackUrl = '';
    
    if (workspaceTeamId) {
      // Use team ID for deep link format: slack://user?team=T1234&id=U5678
      slackUrl = `slack://user?team=${workspaceTeamId}&id=${user.id}`;
    } else {
      // Fallback to web format
      slackUrl = `https://app.slack.com/client/T/D${user.id}`;
    }

    // Try to open in Slack app, fallback to web
    window.location.href = slackUrl;
    
    // Log for debugging
    console.log(`Opening Slack for ${user.displayName}: ${slackUrl}`);
  };

  return (
    <AvatarContainer
      data-tooltip={tooltipText}
      onClick={handleClick}
    >
      <Avatar 
        src={user.avatar} 
        alt={user.displayName}
        $isOffline={!user.isOnline}
        onError={(e) => {
          // Fallback to initials if image fails to load
          const target = e.target as HTMLImageElement;
          const initials = user.displayName
            .split(' ')
            .map(name => name[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
          
          target.style.display = 'none';
          const isOffline = !user.isOnline;
          target.parentElement!.innerHTML = `
            <div style="
              width: 100%;
              height: 100%;
              border-radius: 50%;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 1.2rem;
              border: 3px solid #ffffff;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              opacity: ${isOffline ? '0.4' : '1'};
              filter: ${isOffline ? 'grayscale(70%) brightness(0.8)' : 'none'};
              transition: opacity 0.3s ease, filter 0.3s ease;
            ">${initials}</div>
          `;
        }}
      />
      
      {/* Status emoji badge */}
      {user.statusEmoji && (
        <StatusEmojiBadge $isOffline={!user.isOnline}>
          {displayEmoji}
        </StatusEmojiBadge>
      )}
      
      {/* Clock badge for remote users (non-Mountain Time) */}
      {showClockBadge && compactTime && (
        <ClockBadge $isOffline={!user.isOnline}>
          {compactTime}
        </ClockBadge>
      )}
    </AvatarContainer>
  );
};
